import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Plus,
  Eye,
  Trash2,
  FileSpreadsheet,
  Loader2,
  Filter,
  X,
} from "lucide-react";
import { toast } from "react-hot-toast";
import { clientService } from "../../services/clientService";

const getClientDisplayName = (c) => c?.client?.name || "—";
const getClientDisplayId = (c) => c?.id || "—";
const getClientDisplayContactName = (c) => {
  if (c?.client?.client_type === "business") {
    return c?.business?.contact_name || c?.client?.name || "—";
  }
  return c?.client?.name || "—";
};
const getClientDisplayEmail = (c) => c?.client?.email || "—";
const getClientDisplayPhone = (c) => c?.client?.phone || "—";
const getClientDisplayStatus = (c) => c?.client?.filing_status || "—";
const getClientDisplaySalesRep = (c) => c?.client?.sales_rep_name || "—";
const getClientDisplayClientManager = (c) =>
  c?.client?.client_manager_name || "—";

const normalizeCellValue = (value) => {
  if (value === null || value === undefined) return "—";
  const text = String(value).trim();
  return text.length > 0 ? text : "—";
};

const FILTERABLE_COLUMNS = [
  {
    key: "name",
    label: "Name",
    accessor: (client) => normalizeCellValue(getClientDisplayName(client)),
  },
  {
    key: "clientId",
    label: "Client ID",
    accessor: (client) => normalizeCellValue(getClientDisplayId(client)),
  },
  {
    key: "contactName",
    label: "Contact Name",
    accessor: (client) =>
      normalizeCellValue(getClientDisplayContactName(client)),
  },
  {
    key: "email",
    label: "Email",
    accessor: (client) => normalizeCellValue(getClientDisplayEmail(client)),
  },
  {
    key: "phone",
    label: "Phone Number",
    accessor: (client) => normalizeCellValue(getClientDisplayPhone(client)),
  },
  {
    key: "filingStatus",
    label: "Filing Status",
    accessor: (client) => normalizeCellValue(getClientDisplayStatus(client)),
  },
  {
    key: "salesRep",
    label: "Sales Representative",
    accessor: (client) => normalizeCellValue(getClientDisplaySalesRep(client)),
  },
  {
    key: "clientManager",
    label: "Client Manager",
    accessor: (client) =>
      normalizeCellValue(getClientDisplayClientManager(client)),
  },
];

const FILTERABLE_COLUMN_MAP = FILTERABLE_COLUMNS.reduce((acc, column) => {
  acc[column.key] = column;
  return acc;
}, {});

const ColumnFilter = ({
  columnKey,
  label,
  options = [],
  selectedValues,
  onApply,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [draftSelection, setDraftSelection] = useState(new Set());
  const containerRef = useRef(null);
  const selectAllRef = useRef(null);
  const isActive = Array.isArray(selectedValues);
  const alignLeft = columnKey === "name";

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const baseSelection =
      selectedValues && selectedValues.length > 0 ? selectedValues : options;
    setDraftSelection(new Set(baseSelection));
    setSearchTerm("");
  }, [isOpen, options, selectedValues]);

  useEffect(() => {
    if (!selectAllRef.current) return;
    selectAllRef.current.indeterminate =
      options.length > 0 &&
      draftSelection.size > 0 &&
      draftSelection.size < options.length;
  }, [draftSelection, options]);

  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return options;
    const matcher = searchTerm.trim().toLowerCase();
    return options.filter((option) => option.toLowerCase().includes(matcher));
  }, [options, searchTerm]);

  const toggleOption = (value) => {
    setDraftSelection((prev) => {
      const next = new Set(prev);
      if (next.has(value)) {
        next.delete(value);
      } else {
        next.add(value);
      }
      return next;
    });
  };

  const toggleAll = () => {
    if (draftSelection.size === options.length) {
      setDraftSelection(new Set());
      return;
    }
    setDraftSelection(new Set(options));
  };

  const handleApply = () => {
    if (options.length === 0 || draftSelection.size === options.length) {
      onApply(columnKey, null);
    } else {
      onApply(columnKey, Array.from(draftSelection));
    }
    setIsOpen(false);
  };

  const handleClear = () => {
    onApply(columnKey, null);
    setDraftSelection(new Set(options));
    setIsOpen(false);
  };

  const allSelected =
    options.length > 0 && draftSelection.size === options.length;

  return (
    <div className="relative inline-flex items-center" ref={containerRef}>
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-label={`Filter ${label}`}
        className={`p-1 rounded border border-transparent hover:border-canopy-border transition-colors ${
          isActive ? "text-canopy-blue" : "text-canopy-textMuted"
        }`}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <Filter className="h-4 w-4" />
        <span className="sr-only">Filter {label}</span>
      </button>

      {isOpen && (
        <div
          className={`filter-menu absolute top-full mb-2 w-64 bg-white border border-canopy-border rounded-lg shadow-xl z-20 p-3 ${
            alignLeft ? "left-0" : "right-0"
          }`}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-canopy-text uppercase tracking-wide">
              {label}
            </span>
            <button
              type="button"
              className="text-xs text-canopy-textMuted hover:text-canopy-text"
              onClick={() => setIsOpen(false)}
            >
              Close
            </button>
          </div>

          <label className="flex items-center gap-2 text-sm font-medium mb-2">
            <input
              ref={selectAllRef}
              type="checkbox"
              checked={options.length > 0 && allSelected}
              onChange={toggleAll}
            />
            Select all
          </label>

          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search values..."
            className="w-full border border-canopy-border rounded px-2 py-1 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-canopy-blue"
          />

          <div className="max-h-48 overflow-auto border border-canopy-border rounded">
            {filteredOptions.length === 0 ? (
              <div className="px-2 py-3 text-sm text-canopy-textMuted text-center">
                No matches
              </div>
            ) : (
              filteredOptions.map((option) => (
                <label
                  key={`${columnKey}-${option}`}
                  className="flex items-center gap-2 px-2 py-1 text-sm cursor-pointer hover:bg-gray-50"
                >
                  <input
                    type="checkbox"
                    checked={draftSelection.has(option)}
                    onChange={() => toggleOption(option)}
                  />
                  <span className="truncate" title={option}>
                    {option}
                  </span>
                </label>
              ))
            )}
          </div>

          <div className="flex items-center justify-between pt-3 text-xs">
            <button
              type="button"
              className="text-canopy-textMuted hover:text-canopy-text"
              onClick={handleClear}
            >
              Clear
            </button>
            <button
              type="button"
              className="text-canopy-blue hover:text-canopy-blueHover font-semibold"
              onClick={handleApply}
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const ClientList = ({
  userPermission,
  clients = [],
  loading,
  onClientDeleted,
  searchTerm,
  selectedClientType,
  totalClients = 0,
  currentPage = 1,
  rowsPerPage = 50,
  onClientTypeChange,
  onPageChange,
  onRowsPerPageChange,
  onFiltersChange,
}) => {
  const [activeTab, setActiveTab] = useState(selectedClientType || "all");
  const [exporting, setExporting] = useState(false);
  const [columnFilters, setColumnFilters] = useState({});
  const [salesRepValues, setSalesRepValues] = useState([]);
  const [clientManagerValues, setClientManagerValues] = useState([]);
  const isInactive = (c) => c?.client?.status?.trim() === "Inactive";

  useEffect(() => {
    setActiveTab(selectedClientType || "all");
  }, [selectedClientType]);

  useEffect(() => {
    let cancelled = false;
    const loadLookups = async () => {
      try {
        const [salesReps, clientManagers] = await Promise.all([
          clientService.getSalesReps(""),
          clientService.getClientManagers(""),
        ]);
        if (cancelled) return;
        setSalesRepValues(
          salesReps
            .map((rep) => normalizeCellValue(rep?.name))
            .filter((value) => value && value !== "—"),
        );
        setClientManagerValues(
          clientManagers
            .map((mgr) => normalizeCellValue(mgr?.name))
            .filter((value) => value && value !== "—"),
        );
      } catch (error) {
        console.error("Failed to load lookup values", error);
      }
    };
    loadLookups();
    return () => {
      cancelled = true;
    };
  }, []);

  const tabs = [
    { id: "all", label: "All Clients", count: clients.length },
    {
      id: "person",
      label: "Personal",
      count: clients.filter((c) => c.client?.client_type === "person").length,
    },
    {
      id: "business",
      label: "Corporate",
      count: clients.filter((c) => c.client?.client_type === "business").length,
    },
  ];

  const handleDeleteClient = async (clientId, clientName) => {
    if (userPermission !== "masterAccess") {
      toast.error("You do not have permission to delete clients");
      return;
    }
    if (
      window.confirm(
        `Are you sure you want to delete ${clientName}? This action cannot be undone.`,
      ) &&
      window.confirm(
        `Do you really want to delete ${clientName}? Click “Ok” only if you are absolutely sure.`,
      )
    ) {
      try {
        await clientService.deleteClient(clientId);
        onClientDeleted(clientId);
        toast.success("Client deleted successfully");
      } catch (error) {
        toast.error("Failed to delete client");
        console.error("Delete error:", error);
      }
    }
  };

  const columnValueOptions = useMemo(() => {
    const optionSets = FILTERABLE_COLUMNS.reduce((acc, column) => {
      acc[column.key] = new Set();
      return acc;
    }, {});

    clients.forEach((client) => {
      FILTERABLE_COLUMNS.forEach(({ key, accessor }) => {
        optionSets[key].add(accessor(client));
      });
    });

    Object.entries(columnFilters).forEach(([key, values]) => {
      if (!values || values.length === 0) return;
      if (!optionSets[key]) optionSets[key] = new Set();
      values.forEach((value) => optionSets[key].add(value));
    });

    salesRepValues.forEach((value) => optionSets.salesRep?.add(value));
    clientManagerValues.forEach((value) =>
      optionSets.clientManager?.add(value),
    );

    return Object.fromEntries(
      Object.entries(optionSets).map(([key, set]) => [
        key,
        Array.from(set).sort((a, b) => a.localeCompare(b)),
      ]),
    );
  }, [clients, columnFilters, salesRepValues, clientManagerValues]);

  const filteredClients = useMemo(() => {
    if (!clients.length) return clients;
    return clients.filter((client) =>
      Object.entries(columnFilters).every(([key, values]) => {
        if (!values || values.length === 0) return true;
        const accessor = FILTERABLE_COLUMN_MAP[key]?.accessor;
        if (!accessor) return true;
        return values.includes(accessor(client));
      }),
    );
  }, [clients, columnFilters]);

  // const handleFilterApply = (columnKey, values) => {
  //   setColumnFilters((prev) => {
  //     if (values === null) {
  //       const { [columnKey]: _, ...rest } = prev;
  //       return rest;
  //     }
  //     return { ...prev, [columnKey]: values };
  //   });
  // };

  const handleFilterApply = (columnKey, values) => {
    const next =
      values === null
        ? { ...columnFilters, [columnKey]: undefined }
        : { ...columnFilters, [columnKey]: values };
    onFiltersChange?.(next);

    const serverFilters = {
      salesRep: next.salesRep,
      clientManager: next.clientManager,
    };
    onFiltersChange?.(serverFilters, { resetPage: true });
  };

  // const handleClearAllFilters = () => setColumnFilters({});

  const handleClearAllFilters = () => {
    setColumnFilters({});
    onFiltersChange?.(
      { salesRep: undefined, clientManager: undefined },
      { resetPage: true },
    );
  };

  const activeFilterSummaries = Object.entries(columnFilters).map(
    ([key, values]) => ({
      key,
      label: FILTERABLE_COLUMN_MAP[key]?.label || key,
      values,
    }),
  );
  const hasActiveFilters = activeFilterSummaries.length > 0;
  const showFilteredEmptyState =
    hasActiveFilters && filteredClients.length === 0;
  const safeRows = rowsPerPage || 1;
  const totalPages =
    totalClients === 0 ? 1 : Math.max(1, Math.ceil(totalClients / safeRows));
  const rawPageStart =
    totalClients === 0 ? 0 : (currentPage - 1) * safeRows + 1;
  const displayPageStart =
    totalClients === 0 ? 0 : Math.min(rawPageStart, totalClients);
  const displayPageEnd =
    totalClients === 0
      ? 0
      : filteredClients.length > 0
        ? Math.min(displayPageStart + filteredClients.length - 1, totalClients)
        : displayPageStart;

  const getVisiblePages = () => {
    const maxButtons = 5;
    if (totalPages <= maxButtons) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const half = Math.floor(maxButtons / 2);
    let start = Math.max(1, currentPage - half);
    let end = start + maxButtons - 1;
    if (end > totalPages) {
      end = totalPages;
      start = totalPages - maxButtons + 1;
    }
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const mapTabToClientType = (tab) =>
    tab === "business" ? "business" : tab === "person" ? "person" : undefined;

  const handleExport = async () => {
    try {
      if (userPermission !== "masterAccess") {
        toast.error("You do not have permission to export client information");
        return;
      }
      setExporting(true);
      const blob = await clientService.exportClients({
        search: (searchTerm || "").trim() || undefined,
        client_type: mapTabToClientType(activeTab),
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `clients_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export ready");
    } catch (e) {
      console.error(e);
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const getRowClass = (c) => {
    const status = c?.client?.status?.trim();

    if (status === "Inactive") return "bg-red-100 hover:!bg-red-100";

    return "";
  };

  if (loading && clients.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="canopy-spinner mx-auto mb-4"></div>
          <p className="text-canopy-textMuted">Loading clients...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm animate-fadeIn">
      {/* Tabs */}
      <div className="canopy-tabs px-6 flex items-center gap-2">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => {
              setActiveTab(tab.id);
              onClientTypeChange?.(tab.id);
            }}
            className={`canopy-tab ${activeTab === tab.id ? "all" : ""}`}
          >
            {tab.label}
          </button>
        ))}
        <div className="flex-1" />
        <div className="ml-auto flex items-center gap-3">
          {totalClients > 0 && (
            <span className="px-2 py-0.5 text-xs bg-[#4f7cff] text-white rounded-full">
              {totalClients} {totalClients === 1 ? "client" : "clients"}
            </span>
          )}
          <button
            onClick={handleExport}
            disabled={exporting}
            className="px-3 py-1 text-xs inline-flex items-center disabled:opacity-60"
            title="Export all clients (filters applied)"
            aria-label="Export clients to Excel"
          >
            {exporting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Export to Excel
              </>
            )}
          </button>
        </div>
      </div>

      {hasActiveFilters && (
        <div className="px-6 py-3 border-b border-canopy-border bg-gray-50">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-xs font-semibold uppercase tracking-wide text-canopy-textMuted">
              Active Filters
            </span>
            {activeFilterSummaries.map(({ key, label, values }) => {
              const preview =
                values.length === 0
                  ? "No values"
                  : values.length <= 2
                    ? values.join(", ")
                    : `${values.length} selected`;
              return (
                <span
                  key={key}
                  className="inline-flex items-center gap-2 bg-white border border-canopy-border rounded-full px-3 py-1 text-xs text-canopy-text"
                >
                  <span className="font-medium">{label}:</span>
                  <span
                    className="truncate max-w-[8.5rem]"
                    title={
                      values.length === 0
                        ? "No values selected"
                        : values.join(", ")
                    }
                  >
                    {preview}
                  </span>
                  <button
                    type="button"
                    className="text-canopy-textMuted hover:text-canopy-text"
                    onClick={() => handleFilterApply(key, null)}
                    aria-label={`Clear ${label} filter`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </span>
              );
            })}
            <button
              type="button"
              className="ml-auto text-xs text-canopy-blue hover:text-canopy-blueHover font-medium"
              onClick={handleClearAllFilters}
            >
              Clear all filters
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="canopy-table">
          <thead>
            <tr>
              <th>
                <div className="flex items-center gap-2">
                  <span>Name</span>
                  <ColumnFilter
                    columnKey="name"
                    label="Name"
                    options={columnValueOptions.name || []}
                    selectedValues={columnFilters.name}
                    onApply={handleFilterApply}
                  />
                </div>
              </th>
              <th>
                <div className="flex items-center gap-2">
                  <span>Client ID</span>
                  <ColumnFilter
                    columnKey="clientId"
                    label="Client ID"
                    options={columnValueOptions.clientId || []}
                    selectedValues={columnFilters.clientId}
                    onApply={handleFilterApply}
                  />
                </div>
              </th>
              <th>
                <div className="flex items-center gap-2">
                  <span>Contact Name</span>
                  <ColumnFilter
                    columnKey="contactName"
                    label="Contact Name"
                    options={columnValueOptions.contactName || []}
                    selectedValues={columnFilters.contactName}
                    onApply={handleFilterApply}
                  />
                </div>
              </th>
              <th>
                <div className="flex items-center gap-2">
                  <span>Email</span>
                  <ColumnFilter
                    columnKey="email"
                    label="Email"
                    options={columnValueOptions.email || []}
                    selectedValues={columnFilters.email}
                    onApply={handleFilterApply}
                  />
                </div>
              </th>
              <th>
                <div className="flex items-center gap-2">
                  <span>Phone Number</span>
                  <ColumnFilter
                    columnKey="phone"
                    label="Phone Number"
                    options={columnValueOptions.phone || []}
                    selectedValues={columnFilters.phone}
                    onApply={handleFilterApply}
                  />
                </div>
              </th>
              <th>
                <div className="flex items-center gap-2">
                  <span>Filing Status</span>
                  <ColumnFilter
                    columnKey="filingStatus"
                    label="Filing Status"
                    options={columnValueOptions.filingStatus || []}
                    selectedValues={columnFilters.filingStatus}
                    onApply={handleFilterApply}
                  />
                </div>
              </th>
              <th>
                <div className="flex items-center gap-2">
                  <span>Sales Representative</span>
                  <ColumnFilter
                    columnKey="salesRep"
                    label="Sales Representative"
                    options={columnValueOptions.salesRep || []}
                    selectedValues={columnFilters.salesRep}
                    onApply={handleFilterApply}
                  />
                </div>
              </th>
              <th>
                <div className="flex items-center gap-2">
                  <span>Client Manager</span>
                  <ColumnFilter
                    columnKey="clientManager"
                    label="Client Manager"
                    options={columnValueOptions.clientManager || []}
                    selectedValues={columnFilters.clientManager}
                    onApply={handleFilterApply}
                  />
                </div>
              </th>
              <th className="w-12"></th>
            </tr>
          </thead>
          <tbody>
            {filteredClients.length === 0 ? (
              <tr>
                <td colSpan="9" className="text-center py-12">
                  {showFilteredEmptyState ? (
                    <div className="text-canopy-textMuted">
                      <Filter className="mx-auto h-12 w-12 mb-4" />
                      <h3 className="text-lg font-medium text-canopy-text mb-2">
                        No clients match the selected filters
                      </h3>
                      <p className="mb-6">
                        Try adjusting or clearing your column filters to see
                        more results.
                      </p>
                      <button
                        type="button"
                        onClick={handleClearAllFilters}
                        className="canopy-btn-secondary inline-flex items-center gap-2"
                      >
                        <X className="h-4 w-4" />
                        Clear filters
                      </button>
                    </div>
                  ) : (
                    <div className="text-canopy-textMuted">
                      <Plus className="mx-auto h-12 w-12 mb-4" />
                      <h3 className="text-lg font-medium text-canopy-text mb-2">
                        No clients found
                      </h3>
                      <p className="mb-6">
                        Get started by creating your first client or adjust your
                        search criteria.
                      </p>
                      <Link
                        to="/create"
                        className="canopy-btn-primary inline-flex items-center"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add a Client
                      </Link>
                    </div>
                  )}
                </td>
              </tr>
            ) : (
              filteredClients.map((client) => {
                return (
                  <tr
                    key={client.id}
                    className={`group ${getRowClass(client)}`}
                  >
                    <td>
                      <Link
                        to={`/client/${client.id}`}
                        className={`font-medium ${
                          isInactive(client)
                            ? "text-red-600 hover:text-red-700"
                            : "text-canopy-blue hover:text-canopy-blueHover"
                        }`}
                      >
                        {getClientDisplayName(client)}
                      </Link>
                    </td>
                    <td>
                      <span className="text-canopy-textMuted">
                        {getClientDisplayId(client)}
                      </span>
                    </td>
                    <td>
                      <span className="text-canopy-textMuted">
                        {getClientDisplayContactName(client)}
                      </span>
                    </td>
                    <td>
                      <span className="text-canopy-textMuted">
                        {getClientDisplayEmail(client)}
                      </span>
                    </td>
                    <td>
                      <span className="text-canopy-textMuted">
                        {getClientDisplayPhone(client)}
                      </span>
                    </td>
                    <td>
                      <span className="text-canopy-textMuted">
                        {getClientDisplayStatus(client)}
                      </span>
                    </td>
                    <td>
                      <span className="text-canopy-textMuted">
                        {getClientDisplaySalesRep(client)}
                      </span>
                    </td>
                    <td>
                      <span className="text-canopy-textMuted">
                        {getClientDisplayClientManager(client)}
                      </span>
                    </td>
                    <td>
                      <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Link
                          to={`/client/${client.id}`}
                          className="p-1 text-canopy-textMuted hover:text-canopy-blue rounded transition-colors"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </Link>
                        <button
                          onClick={() =>
                            handleDeleteClient(
                              client.id,
                              getClientDisplayName(client),
                            )
                          }
                          className="p-1 text-canopy-textMuted hover:text-red-600 rounded transition-colors"
                          title="Delete Client"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalClients > 0 && (
        <div className="flex items-center justify-between px-6 py-4 border-t border-canopy-border">
          <div className="flex items-center space-x-2 text-sm text-canopy-textMuted">
            <span>
              Showing {displayPageStart}-{displayPageEnd} of {totalClients}
            </span>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => onPageChange?.(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 text-sm text-canopy-textMuted hover:text-canopy-text disabled:opacity-40"
              aria-label="Previous page"
            >
              ←
            </button>
            {getVisiblePages().map((page) => (
              <button
                key={page}
                onClick={() => onPageChange?.(page)}
                className={`px-3 py-1 text-sm rounded ${
                  page === currentPage
                    ? "bg-canopy-blue text-white"
                    : "text-canopy-textMuted hover:text-canopy-text"
                }`}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() =>
                onPageChange?.(Math.min(totalPages, currentPage + 1))
              }
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-sm text-canopy-textMuted hover:text-canopy-text disabled:opacity-40"
              aria-label="Next page"
            >
              →
            </button>
          </div>

          <div className="flex items-center space-x-2">
            <select
              className="text-sm border border-canopy-border rounded px-2 py-1"
              value={rowsPerPage}
              onChange={(e) => {
                onRowsPerPageChange?.(Number(e.target.value));
              }}
            >
              {[50, 100, 200].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            <span className="text-sm text-canopy-textMuted">per page</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientList;
